import { observable, runInAction } from "mobx"

import { batchInfo } from "@/lib/apiClient/client.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { Model } from "@/lib/Model.ts"

import type { PR_Project2, ProjectKey } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IObservableValue } from "mobx"


export class ProjectListModel extends Model {
  readonly batchSize = 25

  readonly projects: IObservableValue<PR_Project2[]> = observable.box([])

  nextOffset = observable.box<string | null>(null)

  hasMore = observable.box(true)

  constructor(private readonly context: OrgContext) {
    super("ProjectListModel")

    void this.loadInitial()
  }

  private async loadInitial() {
    const [projects, favoriteProjects] = await withGlobalLoader(this.context, Promise.all([
      this.context.queries.listProjectsV2(batchInfo(this.batchSize)),
      this.context.queries.favoriteProjects(),
    ]))

    runInAction(() => {
      this.setProjects(projects.data, favoriteProjects)
      this.hasMore.set(projects.data.length === this.batchSize)
      this.nextOffset.set(projects.next)
    })
  }

  async loadNextBatch() {
    if (!this.nextOffset.get() || this.isLoading.get()) return
    runInAction(() => this.isLoading.set(true))

    try {
      const [projects, favoriteProjects] = await withGlobalLoader(this.context, Promise.all([
        this.context.queries.listProjectsV2(batchInfo(this.batchSize, this.nextOffset.get())),
        this.context.queries.favoriteProjects(),
      ]))

      runInAction(() => {
        this.setProjects(projects.data, favoriteProjects)
        this.hasMore.set(projects.data.length === this.batchSize)
        this.nextOffset.set(projects.next)
      })
    } finally {
      runInAction(() => this.isLoading.set(false))
    }
  }

  private setProjects(data: PR_Project2[], favoriteProjects: ProjectKey[]) {
    const sorted = data.sort((a, b) => {
      const aFavorite = favoriteProjects.some(it => it.key === a.key.key)
      const bFavorite = favoriteProjects.some(it => it.key === b.key.key)

      if (aFavorite !== bFavorite) {
        return Number(bFavorite) - Number(aFavorite)
      }

      return a.name.localeCompare(b.name)
    })

    this.projects.set([...this.projects.get(), ...sorted])
  }
}
